import { CART_MAX_DISTINCT_ITEMS, CART_MAX_QUANTITY_PER_ITEM } from '../../../../shared/cartPolicy';

export { CART_MAX_DISTINCT_ITEMS, CART_MAX_QUANTITY_PER_ITEM };

export type TemperaturePreference = 'chilled' | 'ambient';

export type CartLineInput = {
  id?: unknown;
  productId?: unknown;
  quantity?: unknown;
  temperaturePreference?: unknown;
  temperature_preference?: unknown;
};

export type NormalizedCartLine = {
  id: string;
  productId: string;
  quantity: number;
  temperaturePreference: TemperaturePreference;
};

const toBoundedInt = (value: unknown) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const int = Math.floor(numeric);
  if (int <= 0) return null;
  return Math.min(CART_MAX_QUANTITY_PER_ITEM, int);
};

const sanitizeId = (value: unknown) => String(value ?? '').trim();

export const normalizeTemperaturePreference = (value: unknown): TemperaturePreference => {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'chilled' || raw === 'cold' ? 'chilled' : 'ambient';
};

export const isBeverageCategory = (category: unknown): boolean => {
  const cat = String(category || '').trim().toLowerCase();
  if (
    cat === 'dairy' ||
    cat === 'dairy & eggs' ||
    cat === 'snacks' ||
    cat === 'candy' ||
    cat === 'bakery' ||
    cat === 'frozen' ||
    cat.includes('household') ||
    cat.includes('care')
  ) {
    return false;
  }
  return (
    cat === 'beverages' ||
    cat === 'beverage' ||
    cat === 'drinks' ||
    cat === 'drink' ||
    cat === 'soda' ||
    cat === 'sodas' ||
    cat === 'pop' ||
    cat === 'water' ||
    cat === 'energy drinks' ||
    cat === 'juice' ||
    cat === 'tea' ||
    cat === 'coffee' ||
    cat.includes('drink') ||
    cat.includes('beverage')
  );
};

export const isMultipackBeverage = (nameOrTitle: unknown): boolean => {
  const n = String(nameOrTitle || '').toLowerCase();
  return (
    n.includes('12 pack') ||
    n.includes('12-pack') ||
    n.includes('12pk') ||
    n.includes('12 pk') ||
    n.includes('24 pack') ||
    n.includes('24-pack') ||
    n.includes('24pk') ||
    n.includes('24 pk') ||
    n.includes('8 pack') ||
    n.includes('8-pack') ||
    n.includes('8pk') ||
    n.includes('case of') ||
    n.includes('variety pack') ||
    n.includes('12 ct') ||
    n.includes('24 ct')
  );
};

export const isSingleServeBeverage = (nameOrTitle: unknown, sizeLabel?: unknown): boolean => {
  const n = String(nameOrTitle || '').toLowerCase();
  const s = String(sizeLabel || '').toLowerCase();
  if (isMultipackBeverage(n) || isMultipackBeverage(s)) return false;
  if (n.includes('2l') || n.includes('2 liter') || n.includes('2-liter') || n.includes('gallon')) return false;
  return (
    n.includes('20oz') ||
    n.includes('20 oz') ||
    n.includes('16.9oz') ||
    n.includes('16.9 oz') ||
    n.includes('16oz') ||
    n.includes('16 oz') ||
    n.includes('12oz') ||
    n.includes('12 oz') ||
    n.includes('24oz') ||
    n.includes('24 oz') ||
    n.includes('can') ||
    n.includes('bottle') ||
    n.includes('energy') ||
    s.includes('20 oz') ||
    s.includes('16 oz') ||
    s.includes('12 oz') ||
    s.includes('single')
  );
};

export const isBeverageProduct = (product: {
  category?: unknown;
  name?: unknown;
  sizeLabel?: unknown;
  hasBottleDeposit?: unknown;
}): boolean => {
  const cat = String(product.category || '').trim().toLowerCase();
  const n = String(product.name || '').toLowerCase();
  if (
    /\b(steak|sandwich|sandwiches|pocket|pockets|ice\s*cream|cookie|cookies|candy|chocolates?|chips?|crisps?|crackers?|pretzels?|popcorn|frozen|bars?|gummy|gummies|pop\s*secret|skinny\s*pop|pop-tarts|poptarts|ice\s*pop|popsicle|ring\s*pop|blow\s*pop|cake\s*pop|baking\s*soda|toothpaste|soap|hand\s*soap|dish\s*soap|shampoo|lotion|cleaner|detergent)\b/i.test(
      n
    )
  ) {
    return false;
  }
  // Exclude dairy, milk products, creamers, butter, cheese, yogurt
  if (
    cat === 'dairy' ||
    cat === 'dairy & eggs' ||
    /\b(creamer|creamers|coffee\s*creamer|half\s*&\s*half|half\s*and\s*half|butter|yogurt|cheese|sour\s*cream|whipping\s*cream|heavy\s*cream)\b/i.test(
      n
    ) ||
    /\b(?:whole|skim|lowfat|reduced\s*fat|almond|oat|soy)\s*milk\b/i.test(n)
  ) {
    return false;
  }
  const isDrinkCat = isBeverageCategory(cat);
  const isDrinkName =
    /\b(soda|sodas|pop|cola|coke|pepsi|faygo|vernors|sprite|dew|dr\s*pepper|drink|drinks|beverage|beverages|water|juice|tea|coffee|energy|seltzer|cider|ale|lager|beer|wine|20oz|2l|liter)\b/i.test(
      n
    );
  const isBeverage = isDrinkCat || isDrinkName || Boolean(product.hasBottleDeposit);
  return isBeverage && !isMultipackBeverage(n) && !isMultipackBeverage(product.sizeLabel);
};

/** True when a line should show a chilled/ambient badge (drinks only — not snacks/chips). */
export const shouldShowTemperatureBadge = (input: {
  isBeverage?: boolean | null;
  category?: unknown;
  name?: unknown;
}): boolean => {
  if (input.isBeverage === true) return true;
  if (input.isBeverage === false) return false;
  const n = String(input.name || '').toLowerCase();
  const cat = String(input.category || '').toLowerCase();
  if (
    cat === 'dairy' ||
    cat === 'dairy & eggs' ||
    /\b(creamer|creamers|coffee\s*creamer|half\s*&\s*half|half\s*and\s*half|butter|yogurt|cheese|sour\s*cream|whipping\s*cream|heavy\s*cream)\b/i.test(
      n
    ) ||
    /\b(?:whole|skim|lowfat|reduced\s*fat|almond|oat|soy)\s*milk\b/i.test(n)
  ) {
    return false;
  }
  if (
    n &&
    /\b(steak|sandwich|sandwiches|pocket|pockets|ice\s*cream|cookie|cookies|candy|chocolates?|chips?|crisps?|crackers?|pretzels?|popcorn|frozen|bars?|gummy|gummies|pop\s*secret|skinny\s*pop|pop-tarts|poptarts|ice\s*pop|popsicle|ring\s*pop|blow\s*pop|cake\s*pop|baking\s*soda|toothpaste|soap|hand\s*soap|dish\s*soap|shampoo|lotion|cleaner|detergent)\b/i.test(
      n
    )
  ) {
    return false;
  }
  if (isBeverageCategory(input.category)) return true;
  if (!n) return false;
  return /\b(soda|sodas|pop|cola|coke|pepsi|faygo|vernors|sprite|dew|dr\s*pepper|drink|drinks|beverage|beverages|water|juice|tea|coffee|energy|seltzer|cider|ale|lager|beer|wine|20oz|2l|liter)\b/i.test(
    n
  );
};

export type TemperatureBadgeKind = 'chilled' | 'ambient';

/**
 * Customer-facing temp badge for line items.
 * Drinks: Chilled / Ambient. Non-drinks (chips, snacks): no badge.
 */
export const resolveTemperatureBadge = (input: {
  temperaturePreference?: unknown;
  isBeverage?: boolean | null;
  category?: unknown;
  name?: unknown;
}): { kind: TemperatureBadgeKind; label: string } | null => {
  if (!shouldShowTemperatureBadge(input)) return null;
  const pref = normalizeTemperaturePreference(input.temperaturePreference);
  if (pref === 'chilled') return { kind: 'chilled', label: '❄ Chilled' };
  return { kind: 'ambient', label: '📦 Ambient' };
};

/** Stable client line id: same SKU + same temp share a line; chilled and ambient stay separate. */
export const cartLineId = (productId: string, temperaturePreference: TemperaturePreference): string =>
  `${productId}:${temperaturePreference}`;

/** Strip a trailing `:chilled` / `:ambient` suffix from legacy or compound ids. */
export const stripCartLineTempSuffix = (value: string): string =>
  value.replace(/:(chilled|ambient)$/i, '');

export const resolveCartProductId = (entry: CartLineInput): string => {
  const fromProduct = stripCartLineTempSuffix(sanitizeId(entry.productId));
  if (fromProduct) return fromProduct;
  return stripCartLineTempSuffix(sanitizeId(entry.id));
};

export const normalizeCartLines = (raw: unknown): NormalizedCartLine[] => {
  if (!Array.isArray(raw)) return [];

  const deduped = new Map<string, NormalizedCartLine>();

  for (const entry of raw as CartLineInput[]) {
    const productId = resolveCartProductId(entry);
    if (!productId) continue;

    const quantity = toBoundedInt(entry.quantity);
    if (!quantity) continue;

    const temperaturePreference = normalizeTemperaturePreference(
      entry.temperaturePreference ?? entry.temperature_preference
    );
    const id = cartLineId(productId, temperaturePreference);
    const existing = deduped.get(id);
    if (!existing) {
      deduped.set(id, { id, productId, quantity, temperaturePreference });
      continue;
    }
    deduped.set(id, {
      id,
      productId,
      quantity: Math.min(CART_MAX_QUANTITY_PER_ITEM, existing.quantity + quantity),
      temperaturePreference
    });
  }

  return Array.from(deduped.values()).slice(0, CART_MAX_DISTINCT_ITEMS);
};

export const isHeavyProduct = (product?: {
  isHeavy?: boolean | null;
  handlingPoints?: number | null;
  name?: string | null;
} | null): boolean => {
  if (!product) return false;
  if (product.isHeavy === true) return true;
  if (product.isHeavy === false && (product.handlingPoints === undefined || product.handlingPoints < 3.0)) return false;
  const pts = product.handlingPoints ?? 1.0;
  if (pts >= 3.0) return true;
  const nameLower = String(product.name || '').toLowerCase();
  if (/\b(?:trash|garbage|storage|freezer|sandwich|ziploc)\s+bags?\b/i.test(nameLower)) return false;

  // Solid food packs (eggs, tortillas, cookies, brownies, muffins, bars, sandwiches, hot pockets, candy, chips)
  // must NOT be false-positive heavy:
  const isSolidFoodPack = /\b(?:eggs?|tortillas?|cookies?|brownies?|muffins?|bars?|sandwiches?|pockets?|candy|chips?)\b/i.test(nameLower);
  if (isSolidFoodPack && !/\b(?:24[\s-]*pack|24pk|36[\s-]*pack|36pk|40[\s-]*pack|40pk)\b/i.test(nameLower)) {
    return false;
  }

  const heavyKeywords = [
    '24-pack', '24pk', '36-pack', '36pk', '40-pack', '40pk',
    'case of water', 'water case', '1 gallon', 'softener salt', 'pool salt', 'ice melt',
    'cat litter', 'dog food', 'pet food', 'charcoal', 'firewood', 'bag of ice', 'ice bag'
  ];
  if (heavyKeywords.some(k => nameLower.includes(k))) return true;

  // 12-pack and 18-pack are only heavy for beverages / cans / bottles / liquids
  if (
    /\b(?:12[\s-]*pack|12pk|18[\s-]*pack|18pk)\b/i.test(nameLower) &&
    /\b(?:soda|pop|cola|coke|pepsi|water|drink|drinks|can|cans|bottle|bottles|beer|seltzer|juice|tea|coffee|fl\s*oz)\b/i.test(nameLower)
  ) {
    return true;
  }

  return nameLower.includes('gallon') && !nameLower.includes('half') && !nameLower.includes('1/2');
};

export const isBulkyProduct = (product?: {
  isHeavy?: boolean | null;
  isBulky?: boolean | null;
  handlingPoints?: number | null;
  name?: string | null;
  category?: string | null;
} | null): boolean => {
  if (!product) return false;
  if (isHeavyProduct(product)) return false;
  if (product.isBulky === true) return true;
  const pts = product.handlingPoints ?? 1.0;
  if (pts >= 2.0 && pts < 3.0) return true;
  const nameLower = String(product.name || '').toLowerCase();
  if (/\b(?:trash|garbage|storage|freezer|sandwich|ziploc)\s+bags?\b/i.test(nameLower)) return false;
  const catLower = String(product.category || '').toLowerCase();
  const bulkyKeywords = [
    'toilet paper', 'bath tissue', 'mega roll', 'super roll',
    'paper towel', 'paper towels', 'diaper', 'diapers', 'pull-up', 'pull-ups', 'training pants',
    'napkin', 'napkins', 'paper plate', 'paper plates', 'foam plates',
    'pillow', 'inflatable', 'airbed', 'mattress',
    'bed sheet', 'sheet set', 'pots and pans', 'cookware', 'party size', 'family size',
    '12-pack', '12pk' // Non-heavy multipacks like Hot Pockets 12pk are bulky!
  ];
  return bulkyKeywords.some(k => nameLower.includes(k) || catLower.includes(k));
};
